import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, deleteDoc, getDoc, doc, query, where, writeBatch } from 'firebase/firestore';
import useStreak from '../hooks/useStreak';
import { useApp } from '../context/AppContext';
import CommandPalette from '../components/CommandPalette';
import DonateButton from '../components/DonateButton';
import { useCredits } from '../hooks/useCredits';
import OnboardingTour from '../components/OnboardingTour';
import useIsMobile from '../hooks/useIsMobile';
import useReviewQueue from '../hooks/useReviewQueue';
import useTodaysPlan from '../hooks/useTodaysPlan';
import usePredictedGrade from '../hooks/usePredictedGrade';
import useMasteryTrend from '../hooks/useMasteryTrend';
import AppShell from '../components/AppShell';
import { CailynMark } from '../components/Cailyn';
import Icon from '../components/ui/Icons';
import { matchExamForSubject } from '../data/examConfigs';
import theme, { getTheme } from '../theme';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import PredictedGrade from '../components/ui/PredictedGrade';
import { SkeletonCard } from '../components/ui/LoadingSkeleton';
import { ToastStack, useToast } from '../components/ui/Toast';
import { fetchWithTimeout, isTimeoutError } from '../utils/fetchWithTimeout';
import useBadges from '../hooks/useBadges';

const QUALIFICATIONS = ['AP','SAT','ACT','IB','A-Level','GCSE','IGCSE','CBSE','TOEFL','IELTS','Custom','Other'];
const COLORS = ['#7c3aed','#0891b2','#d4a843','#3dba6e','#e05252','#1a1a1a','#0f766e'];

const Ring = ({ pct, size = 46 }) => {
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);
  const r = (size - 6) / 2, circ = 2 * Math.PI * r;
  const color = pct >= 70 ? t.green : pct >= 45 ? t.gold : t.red;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.border} strokeWidth="4" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
    </svg>
  );
};

function SubjectRow({ subject, prog, weakest, exam, daysUntil, navigate, onDelete }) {
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);
  const trend = useMasteryTrend(subject.id);
  const masteryColor = prog.pct >= 70 ? t.green : prog.pct >= 45 ? t.amber : t.red;
  const subjectColor = subject.color || '#8b6fd4';
  const [hov, setHov] = useState(false);

  return (
    <div
      onClick={() => navigate(subject.isCustom ? `/custom-subject/${subject.id}` : `/subject/${subject.id}`)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? t.surface2 : t.surface,
        border: `1px solid ${t.border}`,
        borderLeft: `4px solid ${subjectColor}`,
        borderRadius: theme.r3,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
        transform: hov ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: hov ? t.shadow2 : t.shadow1,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px' }}>
        {/* Icon circle */}
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: subjectColor + '22', border: `1px solid ${subjectColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 15, fontWeight: 800, color: subjectColor }}>
          {subject.name[0].toUpperCase()}
        </div>
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{subject.name}</span>
            {trend && (
              <span style={{ fontSize: 10, fontWeight: 700, color: trend.direction === 'up' ? t.green : t.red, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Icon name={trend.direction === 'up' ? 'trendUp' : 'trendDown'} size={10} /> {trend.delta}pt
              </span>
            )}
          </div>
          <div style={{ fontSize: 10, color: t.textTert, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subject.qualification}{subject.examBoard ? ` · ${subject.examBoard}` : ''}{exam ? ` · exam in ${daysUntil(exam.date)}d` : ''}
          </div>
          {/* Progress bar */}
          <div style={{ height: 3, background: t.border, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${prog.pct}%`, background: masteryColor, borderRadius: 2, transition: 'width 0.8s ease' }} />
          </div>
        </div>
        {/* Mastery % */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 24, fontWeight: 900, color: prog.total ? masteryColor : t.textTert, letterSpacing: '-1px', width: 52, textAlign: 'right' }}>
            {prog.total ? `${prog.pct}%` : '—'}
          </span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(subject.id, subject.name); }}
            style={{ background: 'none', border: 'none', color: t.textTert, cursor: 'pointer', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}
            onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.color = t.red; }}
            onMouseLeave={e => { e.currentTarget.style.color = t.textTert; }}>
            <Icon name="x" size={12} />
          </button>
        </div>
      </div>
      {/* Quick actions row */}
      <div style={{ display: 'flex', borderTop: `1px solid ${t.border}` }}>
        {[
          { label: 'Lesson', path: subject.isCustom ? `/custom-subject/${subject.id}` : `/subject/${subject.id}` },
          { label: 'Tutor', path: `/tutor/${subject.id}` },
          { label: 'Rush', path: `/rush/${subject.id}` },
        ].map((a, i) => (
          <button
            key={a.label}
            onClick={e => { e.stopPropagation(); navigate(a.path); }}
            style={{
              flex: 1, padding: '8px 4px', fontSize: 11, fontWeight: 600,
              color: t.textTert, background: 'transparent', border: 'none',
              borderRight: i < 2 ? `1px solid ${t.border}` : 'none',
              cursor: 'pointer', transition: 'all 0.12s',
            }}
            onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.color = t.gold; e.currentTarget.style.background = t.goldGlow; }}
            onMouseLeave={e => { e.currentTarget.style.color = t.textTert; e.currentTarget.style.background = 'transparent'; }}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function StudyHeatmap({ userId }) {
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);
  const [sessions, setSessions] = React.useState([]);
  const [loadError, setLoadError] = React.useState(false);
  React.useEffect(() => {
    if (!userId) return;
    import('../firebase/config').then(({ db }) => {
      import('firebase/firestore').then(({ getDocs, collection, query, where }) => {
        getDocs(query(collection(db, 'studySessions'), where('userId', '==', userId)))
          .then(snap => setSessions(snap.docs.map(d => d.data())))
          .catch(() => setLoadError(true));
      });
    });
  }, [userId]);

  const days = [];
  const now = new Date();
  for (let i = 83; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const mins = sessions.filter(s => s.date === key).reduce((a, s) => a + (s.durationSeconds || 0) / 60, 0);
    days.push({ key, mins: Math.round(mins), day: d.getDay() });
  }

  const maxMins = Math.max(...days.map(d => d.mins), 1);
  const goldBase = isDark ? '212,168,67' : '214,178,94';
  const getColor = (mins) => {
    if (mins === 0) return isDark ? 'rgba(255,255,255,0.05)' : 'rgba(17,24,39,0.06)';
    const intensity = mins / maxMins;
    if (intensity < 0.25) return `rgba(${goldBase},0.2)`;
    if (intensity < 0.5) return `rgba(${goldBase},0.45)`;
    if (intensity < 0.75) return `rgba(${goldBase},0.7)`;
    return t.gold;
  };

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  const totalMins = days.reduce((a, d) => a + d.mins, 0);
  const activeDays = days.filter(d => d.mins > 0).length;

  return (
    <div style={{ background: t.surface, borderRadius: theme.r3, border: `1px solid ${t.border}`, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.textTert }}>Study activity · 12 weeks</div>
        <div style={{ fontSize: 11, color: t.textTert }}>
          {activeDays} days active · {totalMins >= 60 ? `${Math.floor(totalMins/60)}h ${totalMins%60}m` : `${totalMins}m`}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        {weeks.map((week, wi) => (
          <div key={wi} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {week.map((day) => (
              <div key={day.key} title={`${day.key}: ${day.mins}m`}
                style={{ width: '100%', height: 13, borderRadius: 3, background: getColor(day.mins), transition: 'transform 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 9, color: t.textTert }}>Less</span>
        {[0, 0.25, 0.5, 0.75, 1].map(v => (
          <div key={v} style={{ width: 10, height: 10, borderRadius: 2, background: getColor(v * 60) }} />
        ))}
        <span style={{ fontSize: 9, color: t.textTert }}>More</span>
      </div>
    </div>
  );
}

const Dashboard = () => {
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);
  const { user } = useAuth();
  const { credits, isPro, isScholar, plan } = useCredits();
  const isMobile = useIsMobile();
  const { weakTopics, addSubjectToState, removeSubjectFromState, buildAIContext, subjects: appSubjects, scores: appScores, recentlyGreenedCount } = useApp();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [topicStatuses, setTopicStatuses] = useState({});
  const [exams, setExams] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [newSubject, setNewSubject] = useState({ name: '', qualification: 'AP', examBoard: '', color: COLORS[0] });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { streak, studiedToday, recordStudy } = useStreak(user?.uid);
  const { getDueCount } = useReviewQueue(user?.uid);
  const { toasts, showToast, dismissToast } = useToast();
  const { items: planItems, toggleItem: togglePlanItem } = useTodaysPlan(user?.uid, { weakTopics, getDueCount, subjects });
  const overallGrade = usePredictedGrade(null);

  const [profileCompletion, setProfileCompletion] = useState(null);
  const [dailyBrief, setDailyBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showWelcome, setShowWelcome] = useState(searchParams.get('welcome') === '1');

  useEffect(() => { document.title = 'Today — Learnable'; return () => { document.title = 'Learnable'; }; }, []);
  useEffect(() => { if (user?.uid) { fetchData(); recordStudy(); } }, [user?.uid]);
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setShowCommandPalette(p => !p); }
      if (e.key === 'Escape') { setShowCommandPalette(false); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, 'userProfile', user.uid)).then(snap => {
      const p = snap.exists() ? snap.data() : {};
      const fields = [p.displayName || user?.displayName, p.school, p.country, p.gradYear, p.learningStyle, p.preferredStudyTime, p.targetUniversities?.filter(u => u.trim()).length > 0, p.motivation, p.weakAreasNote];
      setProfileCompletion(Math.round(fields.filter(Boolean).length / fields.length * 100));
    }).catch(() => { showToast('Failed to load profile', 'error'); });
  }, [user?.uid]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const cached = localStorage.getItem('dailyBrief');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed.date === today) { setDailyBrief(parsed.text); return; }
      } catch {}
    }
    if (!appSubjects || appSubjects.length === 0) return;
    setBriefLoading(true);
    const context = buildAIContext ? buildAIContext() : '';
    fetchWithTimeout('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-6',
        max_tokens: 150,
        system: "You are Cailyn, a warm and encouraging AI study coach. Write a brief, personalised morning message in exactly 3 sentences. Be specific to the student's subjects and situation. Be warm, motivating, and practical. No bullet points — flowing prose only.",
        messages: [{ role: 'user', content: `Today is ${today}. Here is my study context:\n${context}\n\nWrite my daily brief.` }]
      })
    })
    .then(r => r.json())
    .then(data => {
      const text = data?.content?.[0]?.text || null;
      if (text) {
        setDailyBrief(text);
        localStorage.setItem('dailyBrief', JSON.stringify({ date: today, text }));
      }
    })
    .catch((err) => { showToast(isTimeoutError(err) ? 'Daily brief timed out — try again' : 'Failed to load daily brief', 'error'); })
    .finally(() => setBriefLoading(false));
  }, [appSubjects?.length]);

  const fetchData = async () => {
    try {
      const [subSnap, statusSnap, examSnap, chatsSnap, notesSnap] = await Promise.all([
        getDocs(query(collection(db, 'subjects'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'topicStatuses'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'exams'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'chats'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'notes'), where('userId', '==', user.uid))),
      ]);
      setSubjects(subSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const st = {};
      statusSnap.docs.forEach(d => { st[d.data().subjectId + '_' + d.data().topicKey] = d.data().status; });
      setTopicStatuses(st);
      setExams(examSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date)));
      const [scoresSnap] = await Promise.all([
        getDocs(query(collection(db, 'scores'), where('userId', '==', user.uid))),
      ]);
      const act = [
        ...chatsSnap.docs.map(d => ({ type: 'chat', label: `AI Tutor — ${d.data().subjectName || 'General'}`, time: d.data().updatedAt || d.data().createdAt, link: d.data().subjectId ? `/tutor/${d.data().subjectId}?chat=${d.id}` : `/tutor?chat=${d.id}`, icon: 'chat', action: 'Continue' })),
        ...notesSnap.docs.map(d => ({ type: 'note', label: `Note — ${d.data().title || 'Untitled'}`, time: d.data().updatedAt || d.data().createdAt, link: d.data().subjectId ? `/notes/${d.data().subjectId}?note=${d.id}` : `/notes?note=${d.id}`, icon: 'fileText', action: 'Open' })),
        ...scoresSnap.docs.map(d => { const s = d.data(); return { type: 'score', label: `${s.paperName || 'Practice'} — ${s.subject || ''}`, time: s.createdAt || s.date, link: s.subjectId ? `/subject/${s.subjectId}` : '/scores', icon: 'bars', action: 'View', meta: s.percentage != null ? `${s.percentage}%` : null }; }),
      ].filter(a => a.time).sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 6);
      setRecentActivity(act);
    } catch (err) {
      showToast("Couldn't load your dashboard — check your connection and refresh.", 'error', 6000);
    }
    setLoading(false);
  };

  const addSubject = async () => {
    if (!newSubject.name.trim()) { showToast('Give your subject a name first.', 'error'); return; }
    try {
      const ref = await addDoc(collection(db, 'subjects'), { ...newSubject, userId: user.uid, createdAt: new Date().toISOString() });
      const created = { id: ref.id, ...newSubject };
      setSubjects([...subjects, created]);
      addSubjectToState(created);
      setNewSubject({ name: '', qualification: 'AP', examBoard: '', color: COLORS[0] });
      setShowAddModal(false);
      showToast(`${newSubject.name} added`, 'success');
    } catch (err) { showToast("Couldn't add that subject — try again.", 'error'); }
  };

  const deleteSubject = async (id, name) => {
    try {
      const cascadeCollections = ['topicStatuses', 'notes', 'scores', 'reviewQueue', 'flashcards'];
      const snaps = await Promise.all(
        cascadeCollections.map(c => getDocs(query(collection(db, c), where('subjectId', '==', id))))
      );
      const batch = writeBatch(db);
      snaps.forEach(snap => snap.docs.forEach(d => batch.delete(d.ref)));
      batch.delete(doc(db, 'subjects', id));
      await batch.commit();
      setSubjects(subjects.filter(s => s.id !== id));
      removeSubjectFromState(id);
      showToast(`${name || 'Subject'} and all its data removed`, 'info');
    } catch (err) { showToast("Couldn't remove that subject — try again.", 'error'); }
  };

  const getSubjectProgress = (subjectId) => {
    const ss = Object.entries(topicStatuses).filter(([k]) => k.startsWith(subjectId + '_'));
    if (!ss.length) return { pct: 0, green: 0, amber: 0, red: 0, total: 0 };
    const green = ss.filter(([, v]) => v === 'green').length;
    const amber = ss.filter(([, v]) => v === 'amber').length;
    const red = ss.filter(([, v]) => v === 'red').length;
    return { pct: Math.round(((green + amber * 0.5) / ss.length) * 100), green, amber, red, total: ss.length };
  };

  const daysUntil = (date) => Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
  const timeAgo = (iso) => {
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const firstName = (user?.displayName || user?.name || 'there').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const greenCount = Object.values(topicStatuses).filter(s => s === 'green').length;
  const amberCount = Object.values(topicStatuses).filter(s => s === 'amber').length;
  useBadges({ userId: user?.uid, streak, greenCount, profileCompletion: profileCompletion || 0, scoreCount: appScores?.length || 0, recentlyGreenedCount: recentlyGreenedCount || 0, showToast });
  const xp = greenCount * 15 + amberCount * 5 + recentActivity.length * 10 + streak * 5;
  const level = Math.floor(xp / 500) + 1;
  const xpIntoLevel = xp % 500;
  React.useEffect(() => {
    if (user?.uid && xp > 0) {
      localStorage.setItem(`learnable_xp_${user.uid}`, xp);
    }
  }, [xp, user?.uid]);

  const subjectsWithExam = subjects.map(s => ({ subject: s, prog: getSubjectProgress(s.id), exam: exams.find(e => e.subject === s.name) }));
  const urgent = subjectsWithExam
    .filter(s => s.prog.red > 0)
    .sort((a, b) => {
      const aDays = a.exam ? daysUntil(a.exam.date) : 9999;
      const bDays = b.exam ? daysUntil(b.exam.date) : 9999;
      if (aDays !== bDays) return aDays - bDays;
      return b.prog.red - a.prog.red;
    })[0];
  const urgentWeakest = urgent ? weakTopics.find(t => t.subjectId === urgent.subject.id)?.topicKey : null;

  if (loading) {
    return (
      <AppShell>
        <div style={{ padding: isMobile ? '16px' : '44px 52px' }}>
          <SkeletonCard lines={2} /><div style={{ height: 16 }} />
          <SkeletonCard lines={2} /><div style={{ height: 16 }} />
          <SkeletonCard lines={2} />
        </div>
      </AppShell>
    );
  }

  if (subjects.length === 0) {
    return (
      <AppShell>
        <div style={{ maxWidth: '640px', margin: '0 auto', padding: isMobile ? '48px 20px' : '90px 24px', textAlign: 'center', animation: 'fadeUp 0.3s ease forwards' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #8b6fd4, #6b52a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 28px rgba(139,111,212,0.3)', fontSize: 28 }}></div>
          <h1 style={{ fontSize: isMobile ? '24px' : '28px', fontWeight: 700, fontFamily: theme.fontDisplay, color: t.text, letterSpacing: '-0.03em', marginBottom: '10px' }}>
            Hi {firstName}, I'm Cailyn — let's map what you're studying.
          </h1>
          <p style={{ fontSize: '14px', color: t.textSec, lineHeight: 1.7, marginBottom: '28px' }}>
            Add a subject and I'll build a curriculum gap map, find your weak spots, and put together a plan — starting today.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{ padding: '13px 28px', background: t.gold, color: '#0e0c0a', borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: t.goldGlowStrong }}>
            + Add your first subject
          </button>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: '14px', marginTop: '48px', textAlign: 'left' }}>
            {[
              { n: '1', t: 'Add your subjects', d: 'AP, IB, A-Level, GCSE, SAT and more — mapped instantly.' },
              { n: '2', t: 'Rate your confidence', d: 'A quick traffic-light pass tells me what you actually know.' },
              { n: '3', t: 'Get a plan that adapts', d: "I'll surface what to study next, every day, automatically." },
            ].map(s => (
              <div key={s.n} style={{ padding: '18px', borderRadius: theme.r3, border: `1px solid ${t.border}`, background: t.surface }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(212,168,67,0.15)', color: t.gold, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>{s.n}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 4 }}>{s.t}</div>
                <div style={{ fontSize: 12, color: t.textTert, lineHeight: 1.5 }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>

        <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Add Subject" maxWidth="380px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Input label="Subject Name" value={newSubject.name} onChange={e => setNewSubject({ ...newSubject, name: e.target.value })} placeholder="e.g. AP Calculus AB" autoFocus />
            <Input label="Exam Board (optional)" value={newSubject.examBoard} onChange={e => setNewSubject({ ...newSubject, examBoard: e.target.value })} placeholder="e.g. AQA, College Board" />
            <Select label="Qualification" value={newSubject.qualification} onChange={e => setNewSubject({ ...newSubject, qualification: e.target.value })} options={QUALIFICATIONS} />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
            <Button variant="ghost" style={{ flex: 1 }} onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" style={{ flex: 1 }} onClick={addSubject}>Add Subject</Button>
          </div>
        </Modal>
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </AppShell>
    );
  }

  const nextExam = exams[0];

  return (
    <AppShell>
      {showOnboarding && <OnboardingTour onComplete={() => setShowOnboarding(false)} />}
      {showCommandPalette && <CommandPalette subjects={subjects} onClose={() => setShowCommandPalette(false)} />}

      <div style={{ animation: 'fadeUp 0.3s ease forwards' }}>

        {/* ── HERO STRIP ── */}
        <div style={{
          width: '100%',
          padding: isMobile ? '28px 20px' : '36px 40px',
          background: isDark
            ? 'radial-gradient(ellipse at 20% 50%, rgba(139,111,212,0.12) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(212,168,67,0.08) 0%, transparent 60%), #13100e'
            : 'radial-gradient(ellipse at 20% 50%, rgba(109,91,208,0.06) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(214,178,94,0.08) 0%, transparent 60%), #F8F5EC',
          borderBottom: `1px solid ${t.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}>
          {/* Left: greeting + stats */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.gold, marginBottom: 8 }}>
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <h1 style={{
              fontSize: isMobile ? '28px' : '40px',
              fontWeight: 700,
              fontFamily: theme.fontDisplay,
              fontStyle: 'italic',
              color: t.text,
              margin: 0,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}>
              {greeting}, {firstName}.
            </h1>
            <p style={{ fontSize: 14, color: t.textSec, marginTop: 6, marginBottom: 0 }}>Here is what matters most right now.</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.red, padding: '4px 10px', background: t.redGlow, borderRadius: 20, border: `1px solid ${t.red}33` }}>
                 {streak} day streak
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.gold, padding: '4px 10px', background: t.goldGlow, borderRadius: 20, border: `1px solid ${t.goldBorder}` }}>
                 Lvl {level} · {xp.toLocaleString()} XP
              </span>
              {getDueCount() > 0 && (
                <span onClick={() => navigate('/review')} style={{ fontSize: 11, fontWeight: 700, color: t.textSec, padding: '4px 10px', background: t.border, borderRadius: 20, border: `1px solid ${t.border2}`, cursor: 'pointer' }}>
                   {getDueCount()} reviews due
                </span>
              )}
            </div>
          </div>

          {/* Add subject + command palette */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
            {!isMobile && (
              <button onClick={() => setShowCommandPalette(true)}
                style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(17,24,39,0.06)', color: t.textSec, border: `1px solid ${t.border}`, cursor: 'pointer' }}>
                ⌘K
              </button>
            )}
            <button onClick={() => setShowAddModal(true)}
              style={{ padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, background: t.gold, color: '#0e0c0a', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: `0 4px 16px ${t.goldGlowStrong}` }}>
              + Add subject
            </button>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div style={{ padding: isMobile ? '20px 16px' : '28px 40px' }}>

          {/* ── MASTER STATS ROW ── */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Topics Mastered', value: greenCount, sub: `of ${Object.values(topicStatuses).length || '—'} rated`, color: t.green, icon: 'check' },
              { label: 'Day Streak', value: streak, sub: studiedToday ? 'Studied today ✓' : 'Study to extend', color: t.red, icon: 'flame' },
              { label: 'Level', value: `${level}`, sub: `${xpIntoLevel}/500 XP`, color: t.gold, icon: 'sparkle', xpBar: true },
              { label: 'Reviews Due', value: getDueCount() || '0', sub: getDueCount() > 0 ? 'Tap to review' : 'All caught up!', color: getDueCount() > 0 ? t.amber : t.green, icon: 'repeat', clickable: getDueCount() > 0 },
            ].map(stat => (
              <div key={stat.label}
                onClick={stat.clickable ? () => navigate('/review') : undefined}
                style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: theme.r3, padding: '14px 16px', cursor: stat.clickable ? 'pointer' : 'default', transition: 'all 0.15s' }}
                onMouseEnter={e => { if (stat.clickable) { e.currentTarget.style.borderColor = stat.color + '60'; e.currentTarget.style.background = t.surface2; } }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.background = t.surface; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: stat.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={stat.icon} size={13} color={stat.color} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.textTert }}>{stat.label}</span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 900, color: stat.color, letterSpacing: '-1px', lineHeight: 1 }}>{stat.value}</div>
                {stat.xpBar && (
                  <div style={{ height: 3, background: t.border, borderRadius: 2, overflow: 'hidden', marginTop: 6, marginBottom: 2 }}>
                    <div style={{ height: '100%', width: `${(xpIntoLevel / 500) * 100}%`, background: `linear-gradient(90deg, ${t.gold}, #9a7020)`, borderRadius: 2, transition: 'width 0.8s ease' }} />
                  </div>
                )}
                <div style={{ fontSize: 10, color: t.textTert, marginTop: stat.xpBar ? 0 : 4 }}>{stat.sub}</div>
              </div>
            ))}
          </div>

          {/* Profile nudge */}
          {profileCompletion !== null && profileCompletion < 60 && (
            <div onClick={() => navigate('/profile')} style={{ background: t.goldGlow, border: `1px solid ${t.goldBorder}`, borderRadius: theme.r2, padding: '13px 18px', marginBottom: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon name="user" size={15} color={t.gold} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Complete your profile</div>
                  <div style={{ fontSize: 11, color: t.textSec, marginTop: 2 }}>Your profile is {profileCompletion}% complete — Cailyn works better with more context.</div>
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: t.gold, flexShrink: 0 }}>Complete →</span>
            </div>
          )}

          {/* Contextual action nudges — show most relevant one */}
          {(() => {
            const nudgeBannerStyle = { border: `1px solid ${t.goldBorder}`, borderRadius: theme.r2, padding: '13px 18px', marginBottom: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, background: isDark ? 'rgba(200,151,58,0.05)' : t.goldGlow };
            const nudgeTextStyle = { fontSize: 13, fontWeight: 700, color: t.text };
            const nudgeSubStyle = { fontSize: 11, color: t.textSec, marginTop: 2 };
            const nudgeCta = { fontSize: 12, fontWeight: 700, color: t.gold, flexShrink: 0 };
            if (exams.length === 0 && subjects.length > 0) return (
              <div onClick={() => navigate('/planner')} style={nudgeBannerStyle}>
                <Icon name="calendar" size={15} color={t.gold} />
                <div style={{ flex: 1 }}>
                  <div style={nudgeTextStyle}>Set your exam date</div>
                  <div style={nudgeSubStyle}>Cailyn will build a personalised countdown and study schedule.</div>
                </div>
                <span style={nudgeCta}>Set date →</span>
              </div>
            );
            if (subjects.length === 1 && (profileCompletion === null || profileCompletion >= 60)) return (
              <div onClick={() => setShowAddModal(true)} style={nudgeBannerStyle}>
                <Icon name="plus" size={15} color={t.gold} />
                <div style={{ flex: 1 }}>
                  <div style={nudgeTextStyle}>Add another subject</div>
                  <div style={nudgeSubStyle}>Cover all your exams — Cailyn tracks every gap across every subject.</div>
                </div>
                <span style={nudgeCta}>Add →</span>
              </div>
            );
            if ((appScores?.length || 0) === 0 && subjects.length > 0 && (profileCompletion === null || profileCompletion >= 60)) return (
              <div onClick={() => navigate('/scores')} style={nudgeBannerStyle}>
                <Icon name="bars" size={15} color={t.gold} />
                <div style={{ flex: 1 }}>
                  <div style={nudgeTextStyle}>Log your first practice score</div>
                  <div style={nudgeSubStyle}>Track quiz and test results so Learnable can show your real improvement.</div>
                </div>
                <span style={nudgeCta}>Log score →</span>
              </div>
            );
            const hasSATACT = subjects.some(s => ['SAT','ACT'].includes(s.qualification));
            if (hasSATACT) return (
              <div onClick={() => navigate('/test-prep')} style={{ ...nudgeBannerStyle, background: isDark ? 'rgba(139,111,212,0.08)' : 'rgba(109,91,208,0.06)', border: `1px solid ${t.purple}33` }}>
                <Icon name="sparkle" size={15} color={t.purple} />
                <div style={{ flex: 1 }}>
                  <div style={{ ...nudgeTextStyle }}>Your SAT/ACT prep hub is ready</div>
                  <div style={nudgeSubStyle}>Section strategies, score tracking, and timed practice — all in one place.</div>
                </div>
                <span style={{ ...nudgeCta, color: t.purple }}>Open →</span>
              </div>
            );
            return null;
          })()}

          {/* ── TODAY'S PRIORITY (Cailyn — full width, first) ── */}
          <div style={{
            background: isDark
              ? 'linear-gradient(135deg, #16102a 0%, #231850 50%, #16102a 100%)'
              : 'linear-gradient(135deg, #F0EDF8 0%, #E8E3F5 50%, #F0EDF8 100%)',
            border: isDark ? '1px solid rgba(139,111,212,0.25)' : `1px solid rgba(109,91,208,0.2)`,
            borderRadius: theme.r4,
            padding: isMobile ? '20px' : '24px',
            marginBottom: 24,
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: isDark ? 'radial-gradient(circle, rgba(139,111,212,0.25) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(109,91,208,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ display: 'grid', gridTemplateColumns: urgent && !isMobile ? '1fr auto' : '1fr', gap: 24, alignItems: 'center', position: 'relative' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: isDark ? 'rgba(185,155,240,0.7)' : t.purple, marginBottom: 8 }}>Today's Priority</div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: isDark ? '#fff' : t.purple, letterSpacing: '-0.02em', marginBottom: 6, lineHeight: 1.3 }}>
                  {urgent
                    ? (urgent.exam ? `${urgent.subject.name} — exam in ${daysUntil(urgent.exam.date)} days` : `Focus on ${urgent.subject.name}`)
                    : 'No urgent gaps right now'}
                </div>
                <div style={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.55)' : t.textSec, lineHeight: 1.6, marginBottom: 4 }}>
                  {urgent
                    ? (urgentWeakest ? `${urgentWeakest} is your biggest gap.` : `${urgent.prog.red} topic${urgent.prog.red === 1 ? '' : 's'} need attention.`)
                    : briefLoading ? 'Cailyn is preparing your daily brief...' : (dailyBrief || 'Good time to get ahead with a Question Rush.')}
                </div>
                <div style={{ fontSize: 12, color: isDark ? 'rgba(185,155,240,0.55)' : `${t.purple}99`, marginBottom: 16, lineHeight: 1.5 }}>
                  {urgent ? `Focusing here now gives you the biggest exam readiness gain today.` : `You have no red gaps — keep your mastery up with consistent practice.`}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => navigate(urgent ? (urgentWeakest ? `/lesson/${urgent.subject.id}/${encodeURIComponent(urgentWeakest)}` : `/subject/${urgent.subject.id}`) : '/rush')}
                    style={{ padding: '10px 22px', borderRadius: 999, fontSize: 13, fontWeight: 700, background: t.purple, color: '#fff', border: 'none', cursor: 'pointer', boxShadow: t.purpleGlow }}>
                    {urgent ? 'Start 15-Minute Review →' : 'Start Rush →'}
                  </button>
                  <button
                    onClick={() => navigate('/tutor')}
                    style={{ padding: '10px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600, background: 'transparent', color: isDark ? 'rgba(185,155,240,0.8)' : t.purple, border: `1px solid ${isDark ? 'rgba(139,111,212,0.3)' : `${t.purple}44`}`, cursor: 'pointer' }}>
                    Ask Cailyn why →
                  </button>
                </div>
              </div>
              {urgent && !isMobile && (
                <div style={{ textAlign: 'center', padding: '0 16px', borderLeft: `1px solid ${isDark ? 'rgba(139,111,212,0.2)' : 'rgba(109,91,208,0.15)'}` }}>
                  <div style={{ fontSize: 52, fontWeight: 900, color: urgent.prog.pct >= 70 ? t.green : urgent.prog.pct >= 45 ? t.amber : '#e05252', letterSpacing: '-3px', lineHeight: 1 }}>
                    {urgent.prog.pct}%
                  </div>
                  <div style={{ fontSize: 10, color: isDark ? 'rgba(185,155,240,0.5)' : `${t.purple}88`, marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>mastered</div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 10, justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: t.green }}>{urgent.prog.green}</div>
                      <div style={{ fontSize: 9, color: isDark ? 'rgba(255,255,255,0.35)' : t.textTert, fontWeight: 600 }}>ready</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: t.amber }}>{urgent.prog.amber}</div>
                      <div style={{ fontSize: 9, color: isDark ? 'rgba(255,255,255,0.35)' : t.textTert, fontWeight: 600 }}>shaky</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: t.red }}>{urgent.prog.red}</div>
                      <div style={{ fontSize: 9, color: isDark ? 'rgba(255,255,255,0.35)' : t.textTert, fontWeight: 600 }}>gaps</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 20, alignItems: 'start' }}>

            {/* ── LEFT COLUMN ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

              {/* YOUR SUBJECTS */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.textTert }}>Your subjects</div>
                  <button onClick={() => setShowAddModal(true)} style={{ fontSize: 12, fontWeight: 700, color: t.gold, background: 'none', border: 'none', cursor: 'pointer' }}>+ Add</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {subjects.map(subject => {
                    const prog = getSubjectProgress(subject.id);
                    const weakest = weakTopics.find(t => t.subjectId === subject.id)?.topicKey;
                    const exam = exams.find(e => e.subject === subject.name);
                    return (
                      <SubjectRow key={subject.id} subject={subject} prog={prog} weakest={weakest}
                        exam={exam} daysUntil={daysUntil} navigate={navigate} onDelete={deleteSubject} />
                    );
                  })}
                </div>
              </div>

              {/* TODAY'S PLAN */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.textTert }}>Today's plan</div>
                  <span style={{ fontSize: 10, color: t.textTert }}>{planItems.filter(i => i.done).length}/{planItems.length} done</span>
                </div>
                <div style={{ background: t.surface, borderRadius: theme.r3, border: `1px solid ${t.border}`, overflow: 'hidden' }}>
                  {planItems.length === 0 ? (
                    <div style={{ padding: '20px', fontSize: 13, color: t.textTert }}>Nothing planned yet — add a subject to get started.</div>
                  ) : planItems.map((item, i) => (
                    <div key={item.id} onClick={() => navigate(item.link)}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px', cursor: 'pointer', borderBottom: i < planItems.length - 1 ? `1px solid ${t.border}` : 'none', transition: 'background 0.12s', height: 52 }}
                      onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <button onClick={e => { e.stopPropagation(); togglePlanItem(item.id); }}
                        style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${item.done ? t.green : t.border2}`, background: item.done ? t.green : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}>
                        {item.done && <Icon name="check" size={10} color="#fff" strokeWidth={2.6} />}
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: item.done ? t.textTert : t.text, textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</div>
                        {item.sub && <div style={{ fontSize: 11, color: t.textTert, marginTop: 2 }}>{item.sub}</div>}
                      </div>
                      <Icon name="chevronRight" size={13} color={t.textTert} />
                    </div>
                  ))}
                </div>
              </div>

              {/* STUDY HEATMAP */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.textTert, marginBottom: 12 }}>Study activity</div>
                <StudyHeatmap userId={user?.uid} />
              </div>
            </div>

            {/* ── RIGHT COLUMN ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* EXAM READINESS ROADMAP */}
              {exams.length > 0 && (
                <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: theme.r4, padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.textTert }}>Exam roadmap</div>
                    <button onClick={() => navigate('/planner')} style={{ fontSize: 10, fontWeight: 700, color: t.gold, background: 'none', border: 'none', cursor: 'pointer' }}>Planner →</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {exams.slice(0, 4).map(exam => {
                      const sub = subjects.find(s => s.name === exam.subject);
                      const prog = sub ? getSubjectProgress(sub.id) : { pct: 0 };
                      const days = daysUntil(exam.date);
                      const isUrgent = days <= 14;
                      const isClose = days <= 30;
                      const urgencyColor = isUrgent ? t.red : isClose ? t.amber : t.green;
                      const onTrack = prog.pct >= Math.min(90, Math.max(30, 100 - (days / 90) * 60));
                      return (
                        <div key={exam.id} onClick={() => sub && navigate(`/subject/${sub.id}`)} style={{ cursor: sub ? 'pointer' : 'default', padding: '12px', background: t.surface2, borderRadius: 10, border: `1px solid ${t.border}`, transition: 'all 0.15s' }}
                          onMouseEnter={e => { if (sub) e.currentTarget.style.borderColor = urgencyColor + '50'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{exam.subject}</div>
                            <div style={{ flexShrink: 0, marginLeft: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 16, fontWeight: 900, color: urgencyColor, letterSpacing: '-1px' }}>{days}</span>
                              <span style={{ fontSize: 9, color: t.textTert, fontWeight: 600 }}>days</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 10, color: onTrack ? t.green : t.amber, fontWeight: 700 }}>
                              {onTrack ? '✓ On track' : '⚠ Needs work'}
                            </span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: prog.pct >= 70 ? t.green : prog.pct >= 45 ? t.amber : t.red }}>{prog.pct}% ready</span>
                          </div>
                          <div style={{ height: 4, background: t.border, borderRadius: 2 }}>
                            <div style={{ height: '100%', width: `${prog.pct}%`, background: prog.pct >= 70 ? t.green : prog.pct >= 45 ? t.amber : t.red, borderRadius: 2, transition: 'width 0.8s' }} />
                          </div>
                          {isClose && (
                            <button onClick={e => { e.stopPropagation(); const match = sub && matchExamForSubject(sub); navigate(match ? `/exam/${match.id}?subject=${sub.id}` : '/exam'); }}
                              style={{ width: '100%', marginTop: 8, padding: '7px 10px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: 'rgba(139,111,212,0.1)', color: t.purple, border: '1px solid rgba(139,111,212,0.2)', cursor: 'pointer' }}>
                              Take a practice exam →
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {exams.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div style={{ fontSize: 12, color: t.textTert, marginBottom: 10 }}>No exams added yet.</div>
                        <button onClick={() => navigate('/planner')} style={{ fontSize: 11, fontWeight: 700, color: t.gold, background: t.goldGlow, border: `1px solid ${t.goldBorder}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>Add exam dates →</button>
                      </div>
                    )}
                  </div>
                  {exams.length === 0 && (
                    <div style={{ marginTop: 12, padding: '10px 12px', background: t.goldGlow, border: `1px solid ${t.goldBorder}`, borderRadius: 8, fontSize: 11, color: t.textSec }}>
                      Add exam dates in the Study Planner to unlock your personalised readiness roadmap.
                    </div>
                  )}
                </div>
              )}

              {/* CREDITS / PLAN */}
              {isPro ? (
                <div style={{ padding: '12px 16px', borderRadius: theme.r2, background: t.goldGlow, border: `1px solid ${t.goldBorder}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 9, fontWeight: 800, background: `linear-gradient(90deg,${t.gold},${isDark ? '#b8872d' : '#b8912a'})`, color: '#0e0c0a', padding: '3px 8px', borderRadius: 10, letterSpacing: '0.05em', flexShrink: 0 }}>PRO</span>
                  <span style={{ fontSize: 12, color: t.textSec }}>Unlimited AI credits active</span>
                </div>
              ) : credits !== null && (
                <div onClick={() => navigate('/pricing')} style={{ padding: '12px 16px', borderRadius: theme.r2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: credits === 0 ? 'rgba(224,82,82,0.06)' : credits < 8 ? 'rgba(212,168,67,0.06)' : t.surface, border: `1px solid ${credits === 0 ? 'rgba(224,82,82,0.25)' : credits < 8 ? t.goldBorder : t.border}` }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: credits === 0 ? t.red : credits < 8 ? t.gold : t.textSec, marginBottom: 2 }}>
                      {credits === 0 ? 'No credits left' : credits < 8 ? 'Low credits' : 'Free plan'}
                    </div>
                    <div style={{ fontSize: 12, color: t.textTert }}>
                      {credits === 0 ? 'Upgrade to keep using AI' : `${credits} AI credit${credits === 1 ? '' : 's'} remaining`}
                    </div>
                  </div>
                  {!isScholar && <span style={{ fontSize: 11, fontWeight: 700, color: t.gold, flexShrink: 0 }}>Upgrade →</span>}
                </div>
              )}

              {/* QUICK ACTIONS — 2×2 grid */}
              <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: theme.r4, padding: '20px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.textTert, marginBottom: 12 }}>Quick actions</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { icon: 'bolt', label: 'Question Rush', path: '/rush', color: t.gold },
                    { icon: 'repeat', label: 'Review Queue', path: '/review', color: t.purple },
                    { label: 'AI Tutor', path: '/tutor', color: t.purple, isCailyn: true },
                    { icon: 'layers', label: 'Flashcards', path: '/flashcards', color: t.amber },
                    { icon: 'calendar', label: 'Study Planner', path: '/planner', color: t.green },
                    { icon: 'sparkle', label: 'Test Prep', path: '/test-prep', color: t.gold },
                  ].map(item => (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '16px 8px', background: t.surface2, border: `1px solid ${t.border}`, borderRadius: theme.r2, cursor: 'pointer', transition: 'all 0.2s', minHeight: 72 }}
                      onMouseEnter={e => { e.currentTarget.style.background = t.surface3; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = t.shadow2; }}
                      onMouseLeave={e => { e.currentTarget.style.background = t.surface2; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                      {item.isCailyn
                        ? <Icon name="cailyn" size={18} color={item.color} />
                        : <Icon name={item.icon} size={18} color={item.color} />
                      }
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textSec, textAlign: 'center', lineHeight: 1.2 }}>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* RECENT ACTIVITY */}
              {recentActivity.length > 0 && (
                <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: theme.r4, padding: '20px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: t.textTert, marginBottom: 12 }}>Recent activity</div>
                  {recentActivity.slice(0, 4).map((item, i) => (
                    <button key={i} onClick={() => navigate(item.link)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', width: '100%', border: 'none', borderBottom: i < 3 ? `1px solid ${t.border}` : 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: item.type === 'chat' ? t.purpleGlow : item.type === 'score' ? t.goldGlow : t.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name={item.icon || 'fileText'} size={13} color={item.type === 'chat' ? t.purple : item.type === 'score' ? t.gold : t.textTert} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
                        <div style={{ fontSize: 10, color: t.textTert, marginTop: 1 }}>{timeAgo(item.time)}{item.meta ? ` · ${item.meta}` : ''}</div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: t.gold, flexShrink: 0 }}>{item.action} →</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Donate */}
              <div style={{ padding: '12px 14px', background: t.surface2, border: `1px solid ${t.border}`, borderRadius: theme.r2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ fontSize: 11, color: t.textTert, lineHeight: 1.5 }}>Free forever — built by a student</div>
                <DonateButton compact={true} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MODALS ── */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Add Subject" maxWidth="380px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input label="Subject Name" value={newSubject.name} onChange={e => setNewSubject({ ...newSubject, name: e.target.value })} placeholder="e.g. AP Calculus AB" autoFocus />
          <Input label="Exam Board (optional)" value={newSubject.examBoard} onChange={e => setNewSubject({ ...newSubject, examBoard: e.target.value })} placeholder="e.g. AQA, College Board" />
          <Select label="Qualification" value={newSubject.qualification} onChange={e => setNewSubject({ ...newSubject, qualification: e.target.value })} options={QUALIFICATIONS} />
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: t.textSec, marginBottom: 7 }}>Colour</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {COLORS.map(c => (
                <button key={c} onClick={() => setNewSubject({ ...newSubject, color: c })}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: newSubject.color === c ? '2px solid white' : '2px solid transparent', outline: newSubject.color === c ? `2px solid ${c}` : 'none', outlineOffset: 2, cursor: 'pointer', transition: 'transform 0.15s', transform: newSubject.color === c ? 'scale(1.15)' : 'scale(1)' }} />
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" style={{ flex: 1 }} onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" style={{ flex: 1 }} onClick={addSubject}>Add Subject</Button>
        </div>
      </Modal>

      <Modal open={showShareModal} onClose={() => setShowShareModal(false)} title="Share Learnable" maxWidth="380px">
        <p style={{ fontSize: 12, color: t.textTert, marginBottom: 18, marginTop: -10 }}>Help your friends study smarter — free forever.</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: t.surface2, borderRadius: theme.r1, border: `1px solid ${t.border}`, marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: t.textSec }}>{window.location.origin}</span>
          <Button size="sm" variant="primary" onClick={() => { navigator.clipboard.writeText(window.location.origin); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? '✓ Copied' : 'Copy'}
          </Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 14 }}>
          {[
            { label: 'Twitter/X', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Free AI study tool. ${window.location.origin}`)}` },
            { label: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(`${window.location.origin} — free AI study tool`)}` },
            { label: 'Reddit', url: `https://reddit.com/submit?url=${window.location.origin}` },
            { label: 'Email', url: `mailto:?subject=Free AI study tool&body=${window.location.origin}` },
          ].map(p => (
            <a key={p.label} href={p.url} target="_blank" rel="noopener noreferrer"
              style={{ padding: '10px', borderRadius: theme.r1, fontSize: 11, fontWeight: 600, textAlign: 'center', background: t.surface2, color: t.textSec, border: `1px solid ${t.border}`, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {p.label}
            </a>
          ))}
        </div>
        <Button variant="primary" style={{ width: '100%' }} onClick={() => setShowShareModal(false)}>Done</Button>
      </Modal>

      {showWelcome && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 440, background: t.surface, border: `1px solid ${t.border2}`, borderRadius: theme.r5, padding: '36px 32px', boxShadow: t.shadow3, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg, #8b6fd4, #6b52a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 28px rgba(139,111,212,0.35)' }}><Icon name="cailyn" size={32} color="white" /></div>
            <h2 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', color: t.text, marginBottom: 10, fontFamily: theme.fontDisplay }}>
              Welcome to Learnable, {firstName}!
            </h2>
            <p style={{ fontSize: 14, color: t.textSec, lineHeight: 1.7, marginBottom: 24, maxWidth: 340, margin: '0 auto 24px' }}>
              I'm Cailyn — your AI study partner. Add your first subject and I'll map your curriculum, find your gaps, and build you a personalised plan in minutes.
            </p>
            <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
              <button onClick={() => { setShowWelcome(false); searchParams.delete('welcome'); setSearchParams(searchParams); setShowAddModal(true); }}
                style={{ padding: '14px 32px', borderRadius: 12, fontSize: 15, fontWeight: 800, background: 'linear-gradient(135deg, #8b6fd4, #6b52a8)', color: 'white', border: 'none', cursor: 'pointer', boxShadow: '0 8px 24px rgba(139,111,212,0.3)' }}>
                + Add my first subject →
              </button>
              <button onClick={() => { setShowWelcome(false); searchParams.delete('welcome'); setSearchParams(searchParams); }}
                style={{ padding: '10px', borderRadius: 12, fontSize: 13, fontWeight: 600, background: 'none', border: `1px solid ${t.border2}`, color: t.textSec, cursor: 'pointer' }}>
                Explore first
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </AppShell>
  );
};

export default Dashboard;
